/**
 * Screen for viewing previous scan uploads, status, and sommelier feedback.
 */
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../lib/api'
import type { ScanDocument } from '../lib/types'

function formatDate(dateValue?: string | { seconds?: number; _seconds?: number } | null): string {
  if (!dateValue) return ''
  let ms: number | null = null
  if (typeof dateValue === 'string') {
    ms = Date.parse(dateValue)
  } else if (typeof dateValue === 'object') {
    const secs = dateValue.seconds ?? dateValue._seconds
    if (typeof secs === 'number') ms = secs * 1000
  }
  if (!ms || Number.isNaN(ms)) return ''
  return new Date(ms).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export default function History() {
  const scansQuery = useQuery({ queryKey: ['scans'], queryFn: api.listScans })
  const personasQuery = useQuery({ queryKey: ['personas'], queryFn: api.personas })

  const [filterStatus, setFilterStatus] = useState<string | null>(null)

  const personaMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const p of personasQuery.data?.personas ?? []) {
      map.set(p.id, p.name)
    }
    return map
  }, [personasQuery.data])

  const runs = scansQuery.data?.runs ?? []

  const filteredRuns = useMemo(() => {
    if (!filterStatus) return runs
    return runs.filter((run) => run.status === filterStatus)
  }, [runs, filterStatus])

  return (
    <div className="space-y-4 pt-5">
      <div className="flex items-baseline justify-between">
        <h2 className="font-display text-2xl">Previous uploads & feedback</h2>
        <span className="text-xs text-stone-500">
          {runs.length} {runs.length === 1 ? 'scan' : 'scans'}
        </span>
      </div>

      <div className="flex gap-1.5">
        <button
          onClick={() => setFilterStatus(null)}
          className={`chip ${filterStatus === null ? 'chip-on' : ''}`}
        >
          All
        </button>
        <button
          onClick={() => setFilterStatus(filterStatus === 'complete' ? null : 'complete')}
          className={`chip ${filterStatus === 'complete' ? 'chip-on' : ''}`}
        >
          Complete
        </button>
        <button
          onClick={() => setFilterStatus(filterStatus === 'running' ? null : 'running')}
          className={`chip ${filterStatus === 'running' ? 'chip-on' : ''}`}
        >
          Running
        </button>
        <button
          onClick={() => setFilterStatus(filterStatus === 'failed' ? null : 'failed')}
          className={`chip ${filterStatus === 'failed' ? 'chip-on' : ''}`}
        >
          Failed
        </button>
      </div>

      {scansQuery.isLoading && (
        <div className="space-y-3">
          {[0, 1, 2].map((index) => (
            <div key={index} className="card h-32 animate-pulse opacity-40" />
          ))}
        </div>
      )}

      {scansQuery.data && !runs.length && (
        <div className="card space-y-3 p-8 text-center">
          <p className="text-sm text-stone-400">
            No scan history yet. Photograph a wine shelf to get your first recommendations and feedback.
          </p>
          <Link to="/" className="btn-primary inline-flex">
            Start a scan
          </Link>
        </div>
      )}

      {runs.length > 0 && !filteredRuns.length && (
        <p className="pt-10 text-center text-sm text-stone-400">
          No scans match the selected filter.
        </p>
      )}

      <div className="space-y-3">
        {filteredRuns.map((run) => (
          <ScanCard key={run.runId} run={run} personaName={personaMap.get(run.personaId ?? '')} />
        ))}
      </div>
    </div>
  )
}

function ScanCard({ run, personaName }: { run: ScanDocument; personaName?: string }) {
  const targetUrl = run.status === 'complete' ? `/scan/${run.runId}/results` : `/scan/${run.runId}`
  const topWine = run.result?.ranking?.[0] ?? run.ranking?.[0]
  const bottleCount = run.bottlesDetected ?? run.result?.bottles_detected ?? run.cards?.length ?? run.images?.length ?? 0
  const verdict = run.result?.verdict
  const shopping = (run.result?.mode ?? run.mode) === 'shopping'

  return (
    <Link
      to={targetUrl}
      className="card block p-4 transition hover:border-white/20 active:scale-[0.99]"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <StatusBadge status={run.status} />
          {/* Worth saying on the card rather than only inside: a shopping run
              looks like every other scan in this list, and none of its bottles
              are in your cellar. */}
          {shopping && (
            <span className="pill border border-wine-500/40 bg-wine-800/40 text-wine-100">
              Shopping
            </span>
          )}
        </div>
        <span className="text-xs text-stone-500">{formatDate(run.createdAt)}</span>
      </div>

      <div className="mt-3 space-y-1.5">
        <div className="flex items-center justify-between text-xs text-stone-400">
          <span>
            {bottleCount} {bottleCount === 1 ? 'bottle' : 'bottles'} scanned
          </span>
          {personaName && <span>Sommelier: {personaName}</span>}
        </div>

        {shopping && run.context && (
          <p className="text-xs text-stone-500 italic">“{run.context}”</p>
        )}

        {topWine && (
          <p className="text-sm font-medium text-stone-200">
            <span className="text-wine-400 font-display mr-1.5">#1</span>
            {topWine.display_name}
          </p>
        )}

        {verdict && (
          <p className="line-clamp-2 text-xs leading-relaxed text-stone-400">
            {verdict}
          </p>
        )}

        {run.error && (
          <p className="line-clamp-2 text-xs text-rose-400">
            {run.error}
          </p>
        )}
      </div>

      <div className="mt-3 flex items-center justify-end text-xs font-medium text-wine-300">
        <span>{run.status === 'complete' ? 'View verdict & ranking →' : 'View progress →'}</span>
      </div>
    </Link>
  )
}

function StatusBadge({ status }: { status: ScanDocument['status'] }) {
  if (status === 'complete') {
    return <span className="pill bg-emerald-950 text-emerald-300 border border-emerald-800/40">Complete</span>
  }
  if (status === 'running' || status === 'pending') {
    return <span className="pill bg-amber-950 text-amber-300 border border-amber-800/40">In Progress</span>
  }
  return <span className="pill bg-rose-950 text-rose-300 border border-rose-800/40">Failed</span>
}
